import axios from 'axios'
import { t } from '../../../shared/config/translations'
import type { ClientUser } from '../../../shared/types/user'
import { clearAuthAndNavigateToLogin, logout } from '../../api/auth'
import { getSessionSignal } from '../../api/requestSession'
import { resetHistoryData } from '../../api/settings'
import { bindInfoTooltips } from '../../components/InfoTooltip'
import { showSnackbar } from '../../components/Snackbar'
import { invalidateCollectionState } from '../../stores/collectionStore'
import { SettingsAppOverlayController } from './SettingsAppOverlayController'
import { bindSettingsHistoryTransfer } from './settingsHistoryTransfer'
import {
	SETTINGS_HELP_OVERLAY_KEYS,
	type SettingsHelpOverlayKey,
} from './settingsPageTemplates'
import { openSettingsSupportOverlay } from './settingsSupportOverlay'

export class SettingsPageController {
	private readonly removeInfoTooltips: () => void
	private readonly sessionSignal = getSessionSignal()
	private readonly formEl: HTMLFormElement | null
	private readonly appSettingsButtonEl: HTMLButtonElement | null
	private readonly logoutButtonEl: HTMLButtonElement | null
	private readonly resetHistoryButtonEl: HTMLButtonElement | null
	private readonly deleteButtonEl: HTMLButtonElement | null
	private readonly helpOverlayButtonEls: NodeListOf<HTMLButtonElement>
	private readonly appSettingsOverlay: SettingsAppOverlayController

	constructor(rootEl: HTMLElement, user: ClientUser) {
		this.removeInfoTooltips = bindInfoTooltips(rootEl)
		this.formEl = rootEl.querySelector<HTMLFormElement>('#settings-form')
		this.appSettingsButtonEl = rootEl.querySelector<HTMLButtonElement>(
			'[data-app-settings-overlay]',
		)
		this.logoutButtonEl = rootEl.querySelector<HTMLButtonElement>(
			'#settings-logout-btn',
		)
		this.resetHistoryButtonEl = rootEl.querySelector<HTMLButtonElement>(
			'#settings-reset-history-btn',
		)
		this.deleteButtonEl = rootEl.querySelector<HTMLButtonElement>(
			'#settings-delete-btn',
		)
		this.helpOverlayButtonEls = rootEl.querySelectorAll<HTMLButtonElement>(
			'[data-settings-overlay]',
		)
		this.appSettingsOverlay = new SettingsAppOverlayController(user)
		bindSettingsHistoryTransfer(rootEl)

		this.handleAppSettingsOverlayClick =
			this.handleAppSettingsOverlayClick.bind(this)
		this.handleLogout = this.handleLogout.bind(this)
		this.handleResetHistory = this.handleResetHistory.bind(this)
		this.handleDeleteAccount = this.handleDeleteAccount.bind(this)
		this.handleHelpOverlayClick = this.handleHelpOverlayClick.bind(this)

		if (!this.formEl) return
		this.addEventListeners()
	}

	private addEventListeners(): void {
		this.appSettingsButtonEl?.addEventListener(
			'click',
			this.handleAppSettingsOverlayClick,
		)
		this.logoutButtonEl?.addEventListener('click', this.handleLogout)
		this.resetHistoryButtonEl?.addEventListener(
			'click',
			this.handleResetHistory,
		)
		this.deleteButtonEl?.addEventListener('click', this.handleDeleteAccount)
		this.helpOverlayButtonEls.forEach((buttonEl) => {
			buttonEl.addEventListener('click', this.handleHelpOverlayClick)
		})
	}

	show(): void {}
	hide(): void {}
	dispose(): void {
		this.removeInfoTooltips()
		this.appSettingsButtonEl?.removeEventListener(
			'click',
			this.handleAppSettingsOverlayClick,
		)
		this.logoutButtonEl?.removeEventListener('click', this.handleLogout)
		this.resetHistoryButtonEl?.removeEventListener(
			'click',
			this.handleResetHistory,
		)
		this.deleteButtonEl?.removeEventListener(
			'click',
			this.handleDeleteAccount,
		)
		this.helpOverlayButtonEls.forEach((button) =>
			button.removeEventListener('click', this.handleHelpOverlayClick),
		)
	}

	private async handleAppSettingsOverlayClick(): Promise<void> {
		await this.appSettingsOverlay.open()
	}

	private handleLogout(): void {
		void logout().catch((error: unknown) => {
			if (this.sessionSignal.aborted) return
			console.error('Logout failed:', error)
			showSnackbar(t('dialog.errorGeneric'), 'error')
		})
	}

	private async handleResetHistory(): Promise<void> {
		if (!this.resetHistoryButtonEl) return
		const confirmation = window.prompt(t('settings.resetHistoryConfirm'))
		if (confirmation !== 'reset') return

		this.resetHistoryButtonEl.disabled = true
		try {
			await resetHistoryData()
			invalidateCollectionState()
			showSnackbar(t('settings.resetHistorySuccess'), 'default')
		} catch (err) {
			if (this.sessionSignal.aborted) return
			console.error('History reset failed', err)
			showSnackbar(t('dialog.errorGeneric'), 'error')
		} finally {
			this.resetHistoryButtonEl.disabled = false
		}
	}

	private async handleDeleteAccount(): Promise<void> {
		if (!this.deleteButtonEl) return
		const confirmation = window.prompt(t('settings.deleteConfirm'))
		if (confirmation !== 'delete') return

		this.deleteButtonEl.disabled = true
		try {
			await axios.delete('/auth/me')
			await clearAuthAndNavigateToLogin()
		} catch (err) {
			if (this.sessionSignal.aborted) return
			console.error('Account deletion failed', err)
			showSnackbar(t('dialog.errorGeneric'), 'error')
		} finally {
			this.deleteButtonEl.disabled = false
		}
	}

	private handleHelpOverlayClick(event: MouseEvent): void {
		const buttonEl = event.currentTarget
		if (!(buttonEl instanceof HTMLButtonElement)) return

		const overlayKey = buttonEl.dataset.settingsOverlay
		const isSettingsHelpOverlayKey = SETTINGS_HELP_OVERLAY_KEYS.includes(
			overlayKey as SettingsHelpOverlayKey,
		)
		if (!isSettingsHelpOverlayKey) return

		openSettingsSupportOverlay(overlayKey as SettingsHelpOverlayKey)
	}
}
